import { useMutation, useQueryClient } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Badge, type BadgeProps, type Column, Dialog, Table } from "@fronterahealth/frontera-ui-components";

import {
  type TemplateListItem,
  deleteTemplate,
  promoteTemplate,
  updateTemplate,
  useListTemplates,
} from "@api/rest/types-and-hooks";
import { AssignOrgsDialog } from "@components/Templates/AssignOrgsDialog";
import { PublishDialog } from "@components/Templates/PublishDialog";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { REPORT_TYPE_ABBREVIATIONS, type ReportTypeValue } from "@pages/Templates/constants";

const StatusBadge: Record<string, BadgeProps["appearance"]> = {
  Draft: "neutral",
  Published: "success",
  Archived: "error",
};

const EXPORT_FRAMEWORK_LABEL: Record<string, string> = {
  GOOGLE_DOCS: "Google Docs",
  HANDLEBARS: "Typescript",
  DOCXTEMPLATER: "Docx Templater",
};

const EXPORT_FRAMEWORK_OPTIONS = [
  { value: "", label: "All exports" },
  { value: "GOOGLE_DOCS", label: "Google Docs" },
  { value: "HANDLEBARS", label: "Typescript" },
  { value: "DOCXTEMPLATER", label: "Docx Templater" },
];

const REPORT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "INITIAL_ASSESSMENT", label: "IA – Initial Assessment" },
  { value: "REASSESSMENT", label: "RA – Reassessment" },
  { value: "DIAGNOSIS_EVALUATION", label: "DE – Diagnosis Evaluation" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Draft", label: "Draft" },
  { value: "Published", label: "Published" },
];

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 pr-8 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function reportTypeAbbrev(reportType: string): string {
  return REPORT_TYPE_ABBREVIATIONS[reportType as ReportTypeValue] ?? reportType.slice(0, 3).toUpperCase();
}

// Orgs cell with hover tooltip
function OrgsCell({ orgs }: { orgs: TemplateListItem["organizations"] }) {
  const [show, setShow] = useState(false);
  if (!orgs?.length) return <span className="text-text-tertiary">0</span>;
  return (
    <div className="relative inline-block">
      <span
        className="cursor-default text-text-secondary underline decoration-dotted"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {orgs.length}
      </span>
      {show && (
        <div className="absolute z-50 left-0 top-5 min-w-[160px] max-w-[240px] rounded-md border border-border-subtle bg-bg-surface shadow-md p-2">
          {orgs.map((o) => (
            <div key={o.id} className="py-0.5 text-xs truncate">
              <div className="text-text-primary truncate">{o.name}</div>
              {/* INT-79: only shown when ops has configured an explicit EHR sync
                  destination for this org + template pair in Django admin — most
                  pairs have nothing configured and show just the org name. Django
                  only accepts a folder for Intercare and Butterfly Effects Tricare
                  templates (the only sync paths that read it). Wraps rather than
                  truncates so long CentralReach folder names stay readable. */}
              {o.ehr_sync_parent_folder_name && (
                <div className="text-text-tertiary whitespace-normal break-words">
                  EHR sync → {o.ehr_sync_parent_folder_name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface TemplatesTableProps {
  statusFilter?: string;
}

export const TemplatesTable: React.FC<TemplatesTableProps> = ({ statusFilter }) => {
  const go = useNavigate();
  const queryClient = useQueryClient();
  const pageItemsDefault = 20;
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageItemsDefault);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editOrgsTemplate, setEditOrgsTemplate] = useState<TemplateListItem | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null);
  const [publishingTemplate, setPublishingTemplate] = useState<TemplateListItem | null>(null);
  const [exportFilter, setExportFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilterLocal, setStatusFilterLocal] = useState("");

  const [debouncedSearch] = useDebounce(searchTerm, 200);

  // statusFilter prop drives tab-level filtering; statusFilterLocal is the inline dropdown
  const effectiveStatus = statusFilter ?? (statusFilterLocal || undefined);

  const { data, isFetching, refetch } = useListTemplates(
    {
      search: debouncedSearch,
      page,
      page_size: pageSize,
      is_base_or_enterprise: true,
      ...(effectiveStatus ? { status: effectiveStatus } : { exclude_archived: true }),
      ...(exportFilter ? { export_framework: exportFilter } : {}),
      ...(typeFilter ? { report_type: typeFilter } : {}),
    },
    {
      query: {
        queryKey: [
          "templates",
          debouncedSearch,
          page,
          pageSize,
          statusFilter,
          exportFilter,
          typeFilter,
          statusFilterLocal,
        ],
        placeholderData: keepPreviousData,
      },
    },
  );

  const templatesData = data?.status === 200 ? data.data : undefined;
  const templates = templatesData?.results ?? [];
  const totalItemCount = templatesData?.count ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (templateId: number) => deleteTemplate({ template_id: templateId }),
    onSuccess: () => {
      notifySuccess("Template deleted");
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
      void refetch();
    },
    onError: () => notifyError("Failed to delete template"),
  });

  const archiveMutation = useMutation({
    mutationFn: (templateId: number) => updateTemplate({ template_id: templateId, status: "Archived" }),
    onSuccess: () => {
      notifySuccess("Template archived");
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
      void refetch();
    },
    onError: () => notifyError("Failed to archive template"),
  });

  const promoteTemplateMutation = useMutation({
    mutationFn: ({ templateId, previousTemplateId }: { templateId: number; previousTemplateId: number | null }) =>
      promoteTemplate({ template_id: templateId, previous_template_id: previousTemplateId ?? undefined }),
    onSuccess: () => {
      notifySuccess("Template published");
      setPublishingTemplate(null);
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
      void refetch();
    },
    onError: () => notifyError("Failed to publish template"),
  });

  const handlePageChange = (newPage: number, itemsPerPage?: number) => {
    if (itemsPerPage && itemsPerPage !== pageSize) {
      setPageSize(itemsPerPage);
      setPage(1);
    } else {
      setPage(newPage);
    }
  };

  // Column order: Title, Template ID, Type, Export, Orgs, Status, Description, Updated
  const columns: Column<TemplateListItem>[] = [
    {
      dataIndex: "name",
      key: "name",
      title: "Title",
      className: "w-[20%]",
      render: (value: unknown, record: TemplateListItem) => (
        <div className="flex items-center gap-2">
          <span className="text-text-primary truncate">{value as string}</span>
          {record.is_base_template && (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              BASE
            </span>
          )}
        </div>
      ),
    },
    {
      dataIndex: "id",
      key: "id",
      title: "Template ID",
      className: "text-text-secondary w-[8%]",
      render: (value: unknown) => <span className="text-text-secondary">{value as number}</span>,
    },
    {
      dataIndex: "report_type",
      key: "report_type",
      title: "Type",
      className: "w-[5%]",
      render: (value: unknown) => (
        <span className="text-text-secondary font-mono text-xs">{reportTypeAbbrev(value as string)}</span>
      ),
    },
    {
      dataIndex: "export_framework",
      key: "export_framework",
      title: "Export",
      className: "text-text-secondary w-[12%]",
      render: (value: unknown) => (
        <span className="text-text-secondary">
          {value ? (EXPORT_FRAMEWORK_LABEL[value as string] ?? (value as string)) : "—"}
        </span>
      ),
    },
    {
      dataIndex: "organizations",
      key: "organizations",
      title: "Orgs",
      className: "w-[5%]",
      render: (value: unknown) => <OrgsCell orgs={value as TemplateListItem["organizations"]} />,
    },
    {
      dataIndex: "status",
      key: "status",
      title: "Status",
      className: "w-[9%]",
      render: (value: unknown) => {
        const s = value as string;
        return <Badge appearance={StatusBadge[s] ?? "neutral"} text={s} />;
      },
    },
    {
      dataIndex: "description",
      key: "description",
      title: "Description",
      className: "text-text-secondary w-[20%]",
      render: (value: unknown) => (
        <span className="text-text-secondary text-xs truncate block max-w-xs">{(value as string | null) ?? "—"}</span>
      ),
    },
    {
      dataIndex: "last_updated_date",
      key: "last_updated_date",
      title: "Updated",
      className: "w-[14%]",
      render: (value: unknown, record: TemplateListItem) => {
        const dateStr = value ? format(new Date(value as string), "MMM d, yyyy") : "—";
        const name = record.updated_by_name;
        return (
          <span className="text-text-secondary text-xs">
            {name ? `by ${name} ` : ""}
            {dateStr}
          </span>
        );
      },
    },
  ];

  const getActions = (template: TemplateListItem) => {
    const actions: { label: string; textColorClassName?: string; callback: (t: TemplateListItem) => void }[] = [
      {
        label: "View / Edit",
        callback: (t) => go(`/templates/${t.id}`),
      },
      {
        label: "Edit Organizations",
        callback: (t) => setEditOrgsTemplate(t),
      },
    ];

    if (template.status === "Published") {
      actions.push({
        label: "Archive",
        callback: (t) => archiveMutation.mutate(t.id),
      });
    }

    if (template.status === "Draft") {
      actions.push({
        label: "Publish",
        callback: (t) => setPublishingTemplate(t),
      });
      actions.push({
        label: "Delete",
        textColorClassName: "text-text-error-primary",
        callback: (t) => {
          setSelectedTemplate(t);
          setDeleteDialogOpen(true);
        },
      });
    }

    return actions;
  };

  return (
    <>
      {/* Filter bar */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <FilterSelect
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
          options={REPORT_TYPE_OPTIONS}
        />
        <FilterSelect
          value={exportFilter}
          onChange={(v) => {
            setExportFilter(v);
            setPage(1);
          }}
          options={EXPORT_FRAMEWORK_OPTIONS}
        />
        {!statusFilter && (
          <FilterSelect
            value={statusFilterLocal}
            onChange={(v) => {
              setStatusFilterLocal(v);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
          />
        )}
        {(typeFilter || exportFilter || statusFilterLocal) && (
          <button
            type="button"
            className="text-sm text-text-secondary hover:text-text-primary underline"
            onClick={() => {
              setTypeFilter("");
              setExportFilter("");
              setStatusFilterLocal("");
              setPage(1);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="mt-2 flow-root bg-bg-surface w-full">
        <Table
          onRowClick={(t) => go(`/templates/${t.id}`)}
          defaultItemsPerPage={pageItemsDefault}
          showPagination
          loading={isFetching}
          showSearch
          inputClassName="w-96"
          itemsPerPageOptions={[10, 20, 50]}
          actions={getActions}
          data={templates}
          columns={columns}
          onPageChange={handlePageChange}
          searchTerm={searchTerm}
          setSearchTerm={(t) => {
            setSearchTerm(t);
            setPage(1);
          }}
          totalItemCount={totalItemCount}
        />
      </div>

      {/* Delete Dialog */}
      <Dialog
        title={`Delete "${selectedTemplate?.name}"?`}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        primaryButton={{
          text: "Delete",
          onClick: () => {
            if (selectedTemplate) deleteMutation.mutate(selectedTemplate.id);
          },
          disabled: deleteMutation.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setDeleteDialogOpen(false);
            setSelectedTemplate(null);
          },
        }}
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          This will permanently delete the template. This action cannot be undone.
        </p>
      </Dialog>

      {/* Publish Dialog */}
      {publishingTemplate && (
        <PublishDialog
          open={!!publishingTemplate}
          setOpen={(open) => {
            if (!open) setPublishingTemplate(null);
          }}
          templateName={publishingTemplate.name}
          reportType={publishingTemplate.report_type ?? null}
          currentTemplateId={publishingTemplate.id}
          isPending={promoteTemplateMutation.isPending}
          onConfirm={(previousTemplateId) =>
            promoteTemplateMutation.mutate({ templateId: publishingTemplate.id, previousTemplateId })
          }
          onCancel={() => setPublishingTemplate(null)}
        />
      )}

      {/* Edit Organizations Dialog */}
      {editOrgsTemplate && (
        <AssignOrgsDialog
          templateId={editOrgsTemplate.id}
          templateName={editOrgsTemplate.name}
          currentOrgs={editOrgsTemplate.organizations ?? []}
          open={!!editOrgsTemplate}
          onClose={() => setEditOrgsTemplate(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ["templates"] });
            void refetch();
          }}
        />
      )}
    </>
  );
};
